/* eslint-disable max-lines */
import { DSButtonV2 } from '@elliemae/ds-button-v2';
import { Grid } from '@elliemae/ds-grid';
import { css, styled, type Theme } from '@elliemae/ds-system';
import { DSControlledDateTimePickerName, DSControlledDateTimePickerSlots } from '../../../constants/index.js';

// Diagonal line — shape only. Caller supplies the color function.
const makeDiagonalLine = (getColor: (props: { theme: Theme }) => string) => css`
  &:before {
    position: absolute;
    content: '';
    top: 48%;
    left: -2px;
    height: 1px;
    width: calc(100% + 4px);
    transform: rotate(45deg);
    background-color: ${getColor};
  }
`;
// Default: neutral-500, used for active disabled/out-of-range days.
const diagonalLine = makeDiagonalLine(({ theme }) => theme.colors.neutral[500]);

// Focus ring frame — shape only. Colour is always brand-700, injected in StyledWeekDaysListWrapper
// under .focusedDay so a single rule governs the ring colour regardless of day state.
const focusBorder = css`
  &:after {
    position: absolute;
    content: '';
    top: -4px;
    left: -4px;
    width: calc(100% + 8px);
    height: calc(100% + 8px);
    border-radius: 50%;
  }
`;

const backgroundDateRange = {
  inRange: css`
    background-color: ${({ theme }) => theme.colors.brand[100]};
    box-shadow: 4px 0 0 0 ${({ theme }) => theme.colors.brand[100]};
  `,
  startRange: css`
    background-color: ${({ theme }) => theme.colors.brand[100]};
    border-radius: 50% 0 0 50%;
    box-shadow: 4px 0 0 0 ${({ theme }) => theme.colors.brand[100]};
  `,
  endRange: css`
    background-color: ${({ theme }) => theme.colors.brand[100]};
    border-radius: 0 50% 50% 0;
  `,
};

export const StyledIconTriggerButton = styled(DSButtonV2, {
  name: DSControlledDateTimePickerName,
  slot: DSControlledDateTimePickerSlots.PICKER_ICONS.CALENDAR,
})`
  color: ${({ theme }) => theme.colors.brand['800']};
  width: 2rem;
  height: 2rem;
`;

export const StyledShadowWrapper = styled('div', {
  name: DSControlledDateTimePickerName,
  slot: DSControlledDateTimePickerSlots.CONTROLLER_COMPONENT.CALENDAR,
})``;

export const StyledCalendarWrapper = styled.div<{ $readOnlyStyles?: boolean }>`
  display: grid;
  min-width: 260px;
  background-color: ${({ theme, $readOnlyStyles }) =>
    $readOnlyStyles ? theme.colors.neutral['050'] : theme.colors.neutral['000']};
  grid-template-rows: auto auto;
`;

export const StyledHeader = styled.section`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  display: grid;
  padding: ${({ theme }) => theme.space.xxxs};
  grid-template-columns: min-content min-content 1fr min-content min-content;
  grid-template-rows: 1fr;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: ${({ theme }) => theme.colors.neutral['050']};
`;

export const StyledHeaderLabel = styled('h3', {
  name: DSControlledDateTimePickerName,
  slot: DSControlledDateTimePickerSlots.CALENDAR.HEADER_LABEL,
})`
  font-size: 1rem;
`;

export const StyledHeaderButton = styled(DSButtonV2, {
  name: DSControlledDateTimePickerName,
  slot: DSControlledDateTimePickerSlots.CALENDAR.HEADER_BUTTON,
})`
  position: relative;
  width: 2.1538rem;
  height: 2.1538rem;
  &:focus {
    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: 2px solid ${({ theme }) => theme.colors.brand[700]};
      border-radius: 2px;
      pointer-events: none;
    }
  }
  &:hover {
    background: ${({ theme }) => theme.colors.brand[200]};
  }
`;

export const StyledBody = styled.section`
  padding: ${({ theme }) => theme.space.xxxs};
`;

// StyledDayBtn owns shape, cursor, and the focus ring frame.
// Hover colours live in StyledWeekDaysListWrapper using parent-class selectors whose
// specificity (≥0,6,0) beats DSButtonV2's internal transparent hover at (0,2,0).
export const StyledDayBtn = styled(DSButtonV2, {
  name: DSControlledDateTimePickerName,
  slot: DSControlledDateTimePickerSlots.CALENDAR.DAY_BUTTON,
})<{ $readOnlyStyles?: boolean }>`
  height: 2.4615rem;
  width: 2.4615rem;
  border-radius: 50%;
  position: relative;
  border-width: 2px;
  border-style: solid;
  border-color: transparent;
  line-height: 1;
  color: ${({ theme }) => theme.colors.neutral[800]};
  cursor: ${({ $readOnlyStyles }) => ($readOnlyStyles ? 'not-allowed' : 'pointer')};
  display: flex;
  justify-content: center;
  align-items: center;
  &:focus {
    ${focusBorder}
  }
`;

/* eslint-disable @typescript-eslint/no-explicit-any */
export const StyledWeekDaysListWrapper = styled(Grid, {
  name: DSControlledDateTimePickerName,
  slot: DSControlledDateTimePickerSlots.CALENDAR.HEADER_WEEKLY_DAY_LIST,
})<{ isHeader?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.label[400]};
  text-align: center;
  justify-items: center;
  ${({ isHeader, theme }) => (isHeader ? `background: ${theme.colors.neutral['050']}` : '')};
  ${({ isHeader }) => (isHeader ? 'text-transform: capitalize' : '')};

  /* ─────────────────────────────────────────────────────────────────
     SELECTED (valid) — defect fix: brand-600, was brand-700
  ───────────────────────────────────────────────────────────────── */
  .dayWrapper.selectedDay,
  .dayWrapper.startRangeDay,
  .dayWrapper.endRangeDay {
    &:not(.disabledDay):not(.outOfRangeDay):not(.readOnly) {
      ${StyledDayBtn} {
        background-color: ${({ theme }) => theme.colors.brand[600]};
        color: ${({ theme }) => theme.colors.neutral['000']};
        font-weight: ${({ theme }) => theme.fontWeights.semibold};
      }
    }

    /* selected invalid: danger-900 background */
    &.disabledDay,
    &.outOfRangeDay {
      ${StyledDayBtn} {
        background-color: ${({ theme }) => theme.colors.danger[900]};
        color: ${({ theme }) => theme.colors.neutral['000']};
        &:before {
          background-color: ${({ theme }) => theme.colors.neutral['000']};
        }
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────
     INVALID (disabled / out-of-range) — base colour + diagonal
  ───────────────────────────────────────────────────────────────── */
  .dayWrapper.disabledDay,
  .dayWrapper.outOfRangeDay {
    ${StyledDayBtn} {
      cursor: not-allowed;
      color: ${({ theme }) => theme.colors.neutral[700]};
      ${diagonalLine}
      &:before {
        background-color: ${({ theme }) => theme.colors.neutral[500]};
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────
     FOCUS RING — always brand-700 regardless of day state
  ───────────────────────────────────────────────────────────────── */
  .dayWrapper.focusedDay {
    ${StyledDayBtn} {
      ${focusBorder}
      &:after {
        border: 2px solid ${({ theme }) => theme.colors.brand[700]};
      }
    }

    /* focused valid not-readOnly: brand text */
    &:not(.readOnly):not(.disabledDay):not(.outOfRangeDay):not(.selectedDay):not(.startRangeDay):not(.endRangeDay) {
      ${StyledDayBtn} {
        color: ${({ theme }) => theme.colors.brand[600]};
      }
    }

    /* focused invalid not-readOnly: brand text + brand diagonal */
    &.disabledDay:not(.readOnly):not(.selectedDay):not(.startRangeDay):not(.endRangeDay),
    &.outOfRangeDay:not(.readOnly):not(.selectedDay):not(.startRangeDay):not(.endRangeDay) {
      ${StyledDayBtn} {
        color: ${({ theme }) => theme.colors.brand[600]};
        &:before {
          background-color: ${({ theme }) => theme.colors.brand[600]};
        }
      }
    }

    /* focused selected: white text, white inner border (gap), blue outer ring from generic rule */
    &.selectedDay,
    &.startRangeDay,
    &.endRangeDay {
      ${StyledDayBtn} {
        color: ${({ theme }) => theme.colors.neutral['000']};
        border-color: ${({ theme }) => theme.colors.neutral['000']};
      }
    }

    /* focused valid selected (non-readOnly): brand-800 background matches hover */
    &.selectedDay:not(.disabledDay):not(.outOfRangeDay):not(.readOnly),
    &.startRangeDay:not(.disabledDay):not(.outOfRangeDay):not(.readOnly),
    &.endRangeDay:not(.disabledDay):not(.outOfRangeDay):not(.readOnly) {
      ${StyledDayBtn} {
        background-color: ${({ theme }) => theme.colors.brand[800]};
      }
    }

    /* focused selected invalid: white text + white diagonal */
    &.selectedDay.disabledDay,
    &.selectedDay.outOfRangeDay,
    &.startRangeDay.disabledDay,
    &.startRangeDay.outOfRangeDay,
    &.endRangeDay.disabledDay,
    &.endRangeDay.outOfRangeDay {
      ${StyledDayBtn} {
        color: ${({ theme }) => theme.colors.neutral['000']};
        &:before {
          background-color: ${({ theme }) => theme.colors.neutral['000']};
        }
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────
     HOVER BACKGROUNDS — new spec
     Semi-bold on hover for all non-selected states.
     Rules ordered low → high specificity so readOnly always wins.
  ───────────────────────────────────────────────────────────────── */

  /* valid, not selected, not readOnly */
  .dayWrapper:not(.disabledDay):not(.outOfRangeDay):not(.readOnly):not(.selectedDay):not(.startRangeDay):not(
      .endRangeDay
    ) {
    ${StyledDayBtn}:hover {
      background-color: ${({ theme }) => theme.colors.brand[100]};
      color: ${({ theme }) => theme.colors.brand[700]};
      font-weight: ${({ theme }) => theme.fontWeights.semibold};
    }
  }

  /* invalid, not selected, not readOnly */
  .dayWrapper.disabledDay:not(.selectedDay):not(.readOnly),
  .dayWrapper.outOfRangeDay:not(.selectedDay):not(.readOnly) {
    ${StyledDayBtn}:hover {
      /* TODO: danger[200] and danger[400] are currently the same token and both fail contrast
         against common dimsum color combinations. Hardcoded until the theme token is fixed. */
      background-color: #fffcfc; // this is hardcoded because danger 200 fails contrast, pending theme token fix
      font-weight: ${({ theme }) => theme.fontWeights.semibold};
    }
  }

  /* selected valid, not readOnly */
  .dayWrapper.selectedDay:not(.disabledDay):not(.outOfRangeDay):not(.readOnly),
  .dayWrapper.startRangeDay:not(.disabledDay):not(.outOfRangeDay):not(.readOnly),
  .dayWrapper.endRangeDay:not(.disabledDay):not(.outOfRangeDay):not(.readOnly) {
    ${StyledDayBtn}:hover {
      background-color: ${({ theme }) => theme.colors.brand[800]};
    }
  }

  /* selected invalid, not readOnly */
  .dayWrapper.selectedDay.disabledDay:not(.readOnly),
  .dayWrapper.selectedDay.outOfRangeDay:not(.readOnly),
  .dayWrapper.startRangeDay.disabledDay:not(.readOnly),
  .dayWrapper.startRangeDay.outOfRangeDay:not(.readOnly),
  .dayWrapper.endRangeDay.disabledDay:not(.readOnly),
  .dayWrapper.endRangeDay.outOfRangeDay:not(.readOnly) {
    ${StyledDayBtn}:hover {
      background-color: ${({ theme }) => theme.colors.danger[950]};
    }
  }

  /* readOnly, not selected (any validity) — wins over all non-readOnly hover rules */
  .dayWrapper.readOnly:not(.selectedDay):not(.startRangeDay):not(.endRangeDay) {
    ${StyledDayBtn}:hover {
      background-color: ${({ theme }) => theme.colors.neutral[100]};
      color: ${({ theme }) => theme.colors.neutral[700]};
      font-weight: ${({ theme }) => theme.fontWeights.semibold};
    }
  }

  /* ─────────────────────────────────────────────────────────────────
     READONLY REST STATES
  ───────────────────────────────────────────────────────────────── */
  .dayWrapper.selectedDay.readOnly,
  .dayWrapper.startRangeDay.readOnly,
  .dayWrapper.endRangeDay.readOnly {
    ${StyledDayBtn} {
      background-color: ${({ theme }) => theme.colors.neutral['500']};
      font-weight: ${({ theme }) => theme.fontWeights.semibold};
      color: ${({ theme }) => theme.colors.neutral['000']};
      &:before {
        background-color: ${({ theme }) => theme.colors.neutral['000']};
      }
      /* (0,6,0) beats DSButtonV2's .liKnyT:hover { background: transparent } at (0,2,0) */
      &:hover {
        background-color: ${({ theme }) => theme.colors.neutral['700']};
      }
      :focus {
        background-color: ${({ theme }) => theme.colors.neutral['700']};
      }
    }
  }

  .dayWrapper.disabledDay.readOnly,
  .dayWrapper.outOfRangeDay.readOnly {
    ${StyledDayBtn} {
      color: ${({ theme }) => theme.colors.neutral[700]};
      &:before {
        background-color: ${({ theme }) => theme.colors.neutral[700]};
      }
    }
    &.focusedDay {
      ${StyledDayBtn} {
        color: ${({ theme }) => theme.colors.neutral[700]};
        &:before {
          background-color: ${({ theme }) => theme.colors.neutral[700]};
        }
        &:after {
          border: 2px solid ${({ theme }) => theme.colors.brand[700]};
        }
      }
    }
  }

  .dayWrapper.selectedDay.disabledDay.readOnly,
  .dayWrapper.selectedDay.outOfRangeDay.readOnly,
  .dayWrapper.startRangeDay.disabledDay.readOnly,
  .dayWrapper.startRangeDay.outOfRangeDay.readOnly,
  .dayWrapper.endRangeDay.disabledDay.readOnly,
  .dayWrapper.endRangeDay.outOfRangeDay.readOnly {
    ${StyledDayBtn} {
      background-color: ${({ theme }) => theme.colors.neutral['500']};
      color: ${({ theme }) => theme.colors.neutral['000']};
      ${makeDiagonalLine(({ theme }) => theme.colors.neutral['000'])}
      &:hover {
        background-color: ${({ theme }) => theme.colors.neutral['700']};
      }
      :focus {
        background-color: ${({ theme }) => theme.colors.neutral['700']};
      }
    }
  }

  .dayWrapper.inRangeDay.readOnly {
    background-color: ${({ theme }) => theme.colors.neutral['100']};
    box-shadow: 4px 0 0 0 ${({ theme }) => theme.colors.neutral['100']};
  }

  .dayWrapper.inRangeDay.disabledDay.readOnly,
  .dayWrapper.inRangeDay.outOfRangeDay.readOnly {
    ${StyledDayBtn} {
      background-color: transparent;
      color: ${({ theme }) => theme.colors.neutral[700]};
      ${makeDiagonalLine(({ theme }) => theme.colors.neutral[700])}
    }
  }

  .dayWrapper.startRangeDay.inRangeImproperDay.readOnly {
    background-color: ${({ theme }) => theme.colors.neutral['100']};
    box-shadow: 4px 0 0 0 ${({ theme }) => theme.colors.neutral['100']};
  }

  .dayWrapper.endRangeDay.inRangeImproperDay.readOnly {
    background-color: ${({ theme }) => theme.colors.neutral['100']};
  }

  /* ─────────────────────────────────────────────────────────────────
     NOT CURRENT MONTH
  ───────────────────────────────────────────────────────────────── */
  .dayWrapper.notCurrentMonth {
    ${StyledDayBtn} {
      color: ${({ theme }) => theme.colors.neutral[500]};
    }

    &.focusedDay {
      ${StyledDayBtn} {
        color: ${({ theme }) => theme.colors.brand[600]};
      }

      &.disabledDay,
      &.outOfRangeDay {
        ${StyledDayBtn} {
          color: ${({ theme }) => theme.colors.brand[600]};
        }

        &.inRangeDay,
        &.startRangeDay,
        &.endRangeDay {
          ${StyledDayBtn} {
            color: ${({ theme }) => theme.colors.neutral['000']};
          }
        }
      }
    }

    &.selectedDay,
    &.startRangeDay,
    &.endRangeDay {
      ${StyledDayBtn} {
        color: ${({ theme }) => theme.colors.neutral['000']};
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────
     RANGE DAYS
  ───────────────────────────────────────────────────────────────── */
  .dayWrapper.inRangeDay {
    ${backgroundDateRange.inRange}
    &.disabledDay,
    &.outOfRangeDay {
      ${StyledDayBtn} {
        background-color: transparent;
        color: ${({ theme }) => theme.colors.neutral[700]};
        ${makeDiagonalLine(({ theme }) => theme.colors.neutral[700])}
      }
      &.focusedDay {
        ${StyledDayBtn} {
          &:after {
            border-color: ${({ theme }) => theme.colors.brand[700]};
          }
        }
      }
    }
  }

  .dayWrapper.startRangeDay {
    &.inRangeImproperDay {
      ${backgroundDateRange.startRange}
    }
    &.focusedDay {
      ${StyledDayBtn} {
        color: ${({ theme }) => theme.colors.neutral['000']};
      }
    }
    &.disabledDay,
    &.outOfRangeDay {
      ${StyledDayBtn} {
        color: ${({ theme }) => theme.colors.neutral['000']};
        &:before {
          background-color: ${({ theme }) => theme.colors.neutral['000']};
        }
      }
    }
  }

  .dayWrapper.endRangeDay {
    &.inRangeImproperDay {
      ${backgroundDateRange.endRange}
    }
    &.focusedDay {
      ${StyledDayBtn} {
        color: ${({ theme }) => theme.colors.neutral['000']};
      }
    }
    &.disabledDay,
    &.outOfRangeDay {
      ${StyledDayBtn} {
        color: ${({ theme }) => theme.colors.neutral['000']};
        &:before {
          background-color: ${({ theme }) => theme.colors.neutral['000']};
        }
      }
    }
  }

  .dayWrapper.endRangeDay.startRangeDay {
    background-color: transparent;
    box-shadow: none;
  }
`;
/* eslint-enable @typescript-eslint/no-explicit-any */

export const StyledWeekDaysHeaderItem = styled.div`
  min-width: 2.4615rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const CalendarFooterMessage = styled('div', {
  name: DSControlledDateTimePickerName,
  slot: DSControlledDateTimePickerSlots.FOOTERS.CALENDAR,
})<{ $readOnlyStyles?: boolean }>`
  border-top: solid 1px ${({ theme }) => theme.colors.neutral[300]};
  margin: 0 ${({ theme }) => theme.space.xxs2};
  padding: ${({ theme }) => theme.space.xxs2} 0;
  color: ${({ theme }) => theme.colors.danger[900]};
  font-size: ${({ theme }) => theme.fontSizes.microText[200]};
  text-align: right;
  background-color: ${({ theme, $readOnlyStyles }) =>
    $readOnlyStyles ? theme.colors.neutral['050'] : theme.colors.neutral['000']};
`;
